'use client';
import Image from '@/components/Image';
import { IS_DE, IS_FR } from '@/utils/constants';
import { useMediaQuery } from '@mui/material';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React from 'react';
import DE_FillOutPrescriptionClient from './de_page.client';

const picPrefix = '/static-page/guide-tips/fill-out-prescription';

const FillOutPrescriptionClient: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('fillOutPrescription');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleClick = () => {
    router.push('/guide-tips/measure-PD');
  };
  if (IS_DE) {
    // 图片文案有蛮多差异的，写太多条件会让代码复杂化，直接单抽一个出去改。
    return <DE_FillOutPrescriptionClient />;
  }
  // 生成图片路径的辅助函数
  const generateImagePath = (category: string, index: string, hasDesktopVersion = true) => {
    if (isMobile) {
      return `${picPrefix}/${category}-m-${index}.jpg`;
    }
    if (!hasDesktopVersion) {
      return '';
    }
    const localePrefix = IS_FR ? 'fr-' : '';
    return `${picPrefix}/${category}-${localePrefix}pc.jpg`;
  };

  // 图片配置数据
  const imageConfigs = [
    { key: 'singleVision1', category: '1', index: '01', hasDesktop: true },
    { key: 'singleVision2', category: '1', index: '02', hasDesktop: false },
    { key: 'singleVisionWithPrism1', category: '2', index: '01', hasDesktop: true },
    { key: 'singleVisionWithPrism2', category: '2', index: '02', hasDesktop: false },
    { key: 'progressive1', category: '3', index: '01', hasDesktop: true },
    { key: 'progressive2', category: '3', index: '02', hasDesktop: false },
    { key: 'reading1', category: '4', index: '01', hasDesktop: true },
    { key: 'reading2', category: '4', index: '02', hasDesktop: false },
    { key: 'reading3', category: '4', index: '03', hasDesktop: false },
    { key: 'otherInfo1', category: '5', index: '01', hasDesktop: true },
    { key: 'otherInfo2', category: '5', index: '02', hasDesktop: false },
  ];

  // 动态生成图片映射对象
  const initialValue: Record<string, string> = {};
  const prescriptionsImgsMap = imageConfigs.reduce((acc, config) => {
    Object.defineProperty(acc, config.key, {
      get() {
        return generateImagePath(config.category, config.index, config.hasDesktop);
      },
      enumerable: true,
    });
    return acc;
  }, initialValue);
  return (
    <div className="md:w-4/5">
      <h1 className="text-4xl font-bb mb-[48px]">{t('title')}</h1>

      <div className="mb-[85px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">{t('singleVision.title')}</h2>
        <p className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8">
          {t('singleVision.description')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-16 mt-16 md:mt-30">
          <Image
            src={prescriptionsImgsMap.singleVision1}
            alt={t('singleVisionPrescriptionExample1')}
            className="w-full"
            width={800}
            height={600}
          />
          <Image
            src={prescriptionsImgsMap.singleVision2}
            alt={t('singleVisionPrescriptionExample2')}
            className="w-full md:hidden"
            width={800}
            height={600}
          />
        </div>
      </div>

      <div className="mb-[85px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">
          {t('singleVisionWithPrism.title')}
        </h2>
        <p className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8">
          {t('singleVisionWithPrism.description')}
          <br />
          {t.rich('singleVisionWithPrism.description2', {
            b: (chunks) => <span className="font-ib">{chunks}</span>,
          })}
          <span
            onClick={handleClick}
            className="border-b border-primary-purple text-primary-purple cursor-pointer ml-1"
          >
            {t('howToMeasurePD')}
          </span>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-16 md:mt-30">
          <Image
            src={prescriptionsImgsMap.singleVisionWithPrism1}
            alt={t('singleVisionPrescriptionWithPrismExample1')}
            className="w-full"
            width={800}
            height={600}
          />
          <Image
            src={prescriptionsImgsMap.singleVisionWithPrism2}
            alt={t('singleVisionPrescriptionWithPrismExample2')}
            className="w-full  md:hidden"
            width={800}
            height={600}
          />
        </div>
      </div>

      <div className="mb-[85px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">{t('progressive.title')}</h2>
        <ul className="text-[14px] md:text-base    md:text-primary-black leading-[30px] mb-8 space-y-2">
          <li>
            {t('progressive.description')}
            <br />
            {t.rich('progressive.description2', {
              b: (chunks) => <span className="font-ib">{chunks}</span>,
            })}
            <span
              onClick={handleClick}
              className="border-b border-primary-purple text-primary-purple cursor-pointer ml-1"
            >
              {t('howToMeasurePD')}
            </span>
          </li>
          <li className="mb-16">
            {t.rich('progressive.sample1', {
              b: (chunks) => <span className="font-ib">{chunks}</span>,
            })}
            <br />
            {t.rich('progressive.sample2', {
              b: (chunks) => <span className="font-ib">{chunks}</span>,
            })}
            <br />
            {t.rich('progressive.sample3', {
              b: (chunks) => <span className="font-ib">{chunks}</span>,
            })}
          </li>
        </ul>
        <p className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8">
          <strong>{t('progressive.forExample')}</strong>
          <br />
          Near OD +0.75 – Distance OD -1.50 = ADD +2.25
          <br />
          {t.rich('progressive.ps', {
            b: (chunks) => <span className="font-ib">{chunks}</span>,
          })}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-16 mt-16 md:mt-30">
          <Image
            src={prescriptionsImgsMap.progressive1}
            alt={t('progressivePrescriptionExample1')}
            className="w-full"
            width={800}
            height={600}
          />
          <Image
            src={prescriptionsImgsMap.progressive2}
            alt={t('progressivePrescriptionExample2')}
            className="w-full md:hidden"
            width={800}
            height={600}
          />
        </div>
      </div>

      <div className="mb-[85px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">{t('reading.title')}</h2>
        <p className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8">
          {t('reading.description')}
          <br />
          {t.rich('reading.description2', {
            b: (chunks) => <span className="font-ib">{chunks}</span>,
          })}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4 mt-16 md:mt-30">
          <Image
            src={prescriptionsImgsMap.reading1}
            alt={t('readingPrescriptionExample1')}
            className="w-full"
            width={800}
            height={600}
          />
          <Image
            src={prescriptionsImgsMap.reading2}
            alt={t('readingPrescriptionExample2')}
            className="w-full md:hidden"
            width={800}
            height={600}
          />
          <Image
            src={prescriptionsImgsMap.reading3}
            alt={t('readingPrescriptionExample3')}
            className="w-full md:hidden"
            width={800}
            height={600}
          />
        </div>
      </div>

      <div className="mb-[180px]">
        <h2 className="text-[18px] md:text-2xl font-bb mb-[12px]">{t('otherInfo.title')}</h2>
        <ul className="text-[14px] md:text-base text-auxiliary-text md:text-primary-black leading-[30px] mb-8 space-y-2">
          <li>{t('hereAreAFewTermsYouMaySeeOnYourPrescriptionAndWhatTheyMean')}</li>
          <li>{t('odOculusDexterRightEye')}</li>
          <li>{t('osOculusSinisterLeftEye')}</li>
          <li>{t('plPlanoNoSphericalCorrectionNeededEnter0')}</li>
          <li>{t('dsDiopterSphereNoAstigmatismCorrectionForThisEye')}</li>
          <li>
            {t(
              'pleaseMakeSureToEnterAllValuesAsShownInTheExampleAgainIfYourPrescriptionDoesntListAPDYoullNeedToProvideIt',
            )}
            <span
              onClick={handleClick}
              className="border-b border-primary-purple text-primary-purple cursor-pointer ml-1"
            >
              {t('howToMeasurePD')}
            </span>
          </li>
        </ul>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-16 mt-16 md:mt-30">
          <Image
            src={prescriptionsImgsMap.otherInfo1}
            alt={t('otherPrescriptionInformationExample1')}
            className="w-full"
            width={800}
            height={600}
          />
          <Image
            src={prescriptionsImgsMap.otherInfo2}
            alt={t('otherPrescriptionInformationExample2')}
            className="w-full md:hidden"
            width={800}
            height={600}
          />
        </div>
      </div>
    </div>
  );
};

export default FillOutPrescriptionClient;
